import React, { type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { NextSeo } from "next-seo";
import { Box } from "@chakra-ui/react";
import { SkipNavLink, SkipNavContent } from "@chakra-ui/skip-nav";
import { type Banner } from "lib/sanity";
import { getPageMetaImage } from "utils";
import { Global } from "@emotion/react";
import { ColorThemeProvider, type ColorThemePageProps } from "contexts/color-theme";
import { HeaderSimple } from "./header-simple";
import { Footer, type FooterProps } from "./footer";
import { Header } from "./header/index";

const defaultTitle = "Hightouch | Start syncing data in minutes";

export type PageProps = {
  /**
   * If `noIndex` is true, be sure to exclude the page from the sitemap in `next-sitemap.config.js`
   */
  noIndex?: boolean; // eslint-disable-line react/boolean-prop-naming
  canonicalUrl?: string;
  title: string;
  description: string;
  image?: { url: string; width: number; height: number; alt: string };
  announcementBanner?: Banner;
  headerVariant?: "full" | "simple";
  hasTransparentHeader?: boolean;
  hasSmoothScroll?: boolean;
  children: ReactNode;
  footerOptions?: FooterProps;
} & Partial<ColorThemePageProps>;

export function Page({
  noIndex,
  canonicalUrl,
  title,
  description,
  image,
  announcementBanner,
  children,
  hasSmoothScroll,
  headerVariant = "full",
  hasTransparentHeader = false,
  theme = "light",
  bgColor = "white",
  footerOptions,
}: PageProps) {
  // During server side compilation of pages for Algolia `getServerState`, useSearchParams may be undefined
  const pathname = usePathname();

  const getUrl = () => {
    if (pathname === "/") {
      return `https://hightouch.com`;
    }

    return `https://hightouch.com${pathname}`;
  };

  const getCanonical = () => {
    if (canonicalUrl) return canonicalUrl;

    return getUrl();
  };

  const renderHeader = () => {
    if (headerVariant === "full") {
      return <Header announcementBanner={announcementBanner} isTransparent={hasTransparentHeader} />;
    }

    if (headerVariant === "simple") {
      return <HeaderSimple isTransparent={hasTransparentHeader} />;
    }

    return null;
  };

  return (
    <ColorThemeProvider initialTheme={theme} initialBgColor={bgColor}>
      <NextSeo
        title={title ? (pathname === "/" ? title : `${title} | Hightouch`) : defaultTitle}
        description={description}
        canonical={getCanonical()}
        noindex={noIndex}
        nofollow={noIndex}
        twitter={{
          cardType: "summary_large_image",
        }}
        openGraph={{
          type: "website",
          url: getCanonical(),
          title,
          description,
          images: [image ?? getPageMetaImage()],
        }}
      />

      <SkipNavLink id="main-content">Skip to main content</SkipNavLink>
      {renderHeader()}

      <Box as="main" pos="relative">
        <SkipNavContent id="main-content" />

        {children}
      </Box>

      <Footer {...footerOptions} />

      {hasSmoothScroll && <Global styles="html { scroll-behavior: smooth; }" />}
    </ColorThemeProvider>
  );
}
